import { useEffect, useState } from 'react';
import api from '../api/axios';
import DoctorPatientInfo from './DoctorPatientInfo';
import { toast } from 'react-toastify';

function PatientRecordsModal({ appointment, onClose }) {
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    const load = async () => {
      if (!appointment?.appointment_id) return;
      try {
        const res = await api.get(
          `prescriptions/doctor/appointment/${appointment.appointment_id}/patient-records/`
        );
        setPatient(res.data.patient);
        setRecords(res.data.records || []);
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to load patient records');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [appointment?.appointment_id]);

  const mediaUrl = (file) => {
    if (!file) return null;
    if (file.startsWith('http')) return file;
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return file.startsWith('/') ? `${base}${file}` : `${base}/${file}`;
  };

  if (!appointment) return null;

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content border-0 shadow-lg rounded-4">
          <div className="modal-header border-0">
            <h5 className="modal-title fw-bold">
              <i className="bi bi-folder2-open me-2" />
              Patient health records
            </h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          <div className="modal-body">
            <div className="mb-3 p-3 bg-light rounded-3">
              <DoctorPatientInfo appointment={{ ...appointment, ...patient }} />
              {appointment.patient_notes && (
                <div className="mt-2 pt-2 border-top">
                  <p className="text-muted small fw-bold mb-1"><i className="bi bi-chat-left-text text-primary me-1" /> Patient Notes / Symptoms:</p>
                  <p className="mb-0 small text-dark fst-italic">"{appointment.patient_notes}"</p>
                </div>
              )}
              {(appointment.medical_reports?.length
                ? appointment.medical_reports
                : (appointment.medical_report ? [appointment.medical_report] : [])
              ).map((reportUrl, idx) => (
                <div
                  key={`${reportUrl}-${idx}`}
                  className="mt-2 pt-2 border-top d-flex justify-content-between align-items-center gap-2"
                >
                  <span className="text-muted small fw-bold">
                    <i className="bi bi-paperclip text-success me-1" />
                    {appointment.medical_reports?.length > 1
                      ? `Attached Report ${idx + 1}:`
                      : 'Attached Booking Report:'}
                  </span>
                  <a
                    href={mediaUrl(reportUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-success rounded-pill px-3"
                  >
                    <i className="bi bi-file-earmark-medical me-1" /> Open File
                  </a>
                </div>
              ))}
            </div>
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : records.length === 0 ? (
              <p className="text-muted text-center py-4">No documents on file for this patient.</p>
            ) : (
              <div className="list-group list-group-flush">
                {records.map((rec) => (
                  <div key={rec.id} className="list-group-item px-0 border-bottom">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <h6 className="fw-bold mb-1">{rec.title}</h6>
                        <p className="text-muted small mb-1">
                          {rec.document_type} · {rec.source}
                        </p>
                        {rec.description && (
                          <p className="small text-muted mb-1">{rec.description}</p>
                        )}
                        <p className="text-muted small mb-0">
                          {rec.uploaded_at
                            ? new Date(rec.uploaded_at).toLocaleDateString()
                            : '—'}
                        </p>
                      </div>
                      {rec.file && (
                        <a
                          href={mediaUrl(rec.file)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary rounded-pill"
                        >
                          Open
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="modal-footer border-0">
            <button type="button" className="btn btn-primary rounded-pill" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PatientRecordsModal;
